import { createPortal } from "react-dom";
import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { fgPortalMount } from "../../lib/fgPortalMount";
import { MONTH_NAMES, formatYmdLong } from "../../lib/dates";
import type { CatalogEventDTO } from "../../lib/eventsApi";
import { searchCatalogEvents, browseCatalogEvents } from "../../lib/eventsApi";
import type { RaceSource } from "../../lib/raceSources";
import { LucideIcon } from "../FG/LucideIcon";
import "./onboarding.css";

/** "Oct 25, 2026" or, for multi-day weekends, "Oct 22–25, 2026". UTC slices. */
function formatCatalogDateLine(startIso: string, endIso: string | null): string {
  const s = new Date(startIso);
  if (!Number.isFinite(s.getTime())) return "";
  const start = `${MONTH_NAMES[s.getUTCMonth()]} ${s.getUTCDate()}, ${s.getUTCFullYear()}`;
  const e = endIso ? new Date(endIso) : null;
  if (!e || !Number.isFinite(e.getTime())) return start;
  const sameMonth = e.getUTCMonth() === s.getUTCMonth() && e.getUTCFullYear() === s.getUTCFullYear();
  const endPart = sameMonth
    ? `${e.getUTCDate()}, ${e.getUTCFullYear()}`
    : `${MONTH_NAMES[e.getUTCMonth()]} ${e.getUTCDate()}, ${e.getUTCFullYear()}`;
  return `${MONTH_NAMES[s.getUTCMonth()]} ${s.getUTCDate()}–${endPart}`;
}

/** UTC calendar line for list subtitle (date · distances · location). */
function formatCatalogEventSubtitle(dto: CatalogEventDTO): string {
  const dateLine = formatCatalogDateLine(dto.startDate, dto.endDate);
  const distanceLabel =
    dto.distances.length > 0 ? dto.distances.map((d) => d.label).join(" · ") : "Race";
  const locationLabel = dto.location?.trim() || "—";
  return `${dateLine} · ${distanceLabel} · ${locationLabel}`;
}

const MIN_QUERY_LEN = 2;
const DEBOUNCE_MS = 280;
const PANEL_HEIGHT_CAP = 320;
const PANEL_MIN_HEIGHT = 160;
const PANEL_HEIGHT_CAP_MOBILE = 380;

function measureFloatingPanel(anchor: DOMRect): {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: "above" | "below";
} {
  const pad = 8;
  const gap = 0;
  const seamOverlap = 2;
  const viewportTop = window.visualViewport?.offsetTop ?? 0;
  const viewportH = window.visualViewport?.height ?? window.innerHeight;
  const viewportBottom = viewportTop + viewportH;
  const width = Math.max(1, Math.round(anchor.width));
  let left = Math.round(anchor.left);
  if (left + width > window.innerWidth - pad) {
    left = Math.max(pad, window.innerWidth - pad - width);
  }
  if (left < pad) left = pad;

  const spaceBelow = viewportBottom - anchor.bottom - pad;
  const spaceAbove = anchor.top - viewportTop - pad;
  const placement: "below" | "above" =
    spaceBelow >= PANEL_MIN_HEIGHT
      ? "below"
      : spaceAbove >= PANEL_MIN_HEIGHT
        ? "above"
        : spaceBelow >= spaceAbove
          ? "below"
          : "above";
  const available = Math.max(
    PANEL_MIN_HEIGHT,
    (placement === "below" ? spaceBelow : spaceAbove) - gap
  );
  const heightCap =
    typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches
      ? PANEL_HEIGHT_CAP_MOBILE
      : PANEL_HEIGHT_CAP;
  const maxHeight = Math.min(heightCap, available);
  const top =
    placement === "below"
      ? Math.round(anchor.bottom - seamOverlap)
      : Math.max(
          viewportTop + pad,
          Math.round(anchor.top - maxHeight + (placement === "above" ? seamOverlap : gap))
        );

  return { top, left, width, maxHeight, placement };
}

/**
 * A race the principal already has on their profile (customer event) or holds
 * client-side (anon pending race), surfaced as a pinned "Your races" choice at
 * the top of the dropdown. Picking one reuses the existing event — it is never
 * duplicated. Exactly one identifier (`customerEventId` / `eventCatalogId`) is
 * usually set; both are optional so a custom anon race (name + date only) works.
 */
export type PinnedRaceOption = {
  name: string;
  /** `YYYY-MM-DD`. */
  dateYmd: string;
  eventCatalogId?: string;
  distanceLabel?: string;
  customerEventId?: string;
  /** Where the race came from — drives the muted provenance sub-line (spec §3.4). */
  source?: RaceSource;
};

/** "Oct 25, 2026 · Marathon" — date plus the chosen distance when present. */
function formatPinnedRaceMeta(race: PinnedRaceOption): string {
  const date = formatYmdLong(race.dateYmd);
  return race.distanceLabel ? `${date} · ${race.distanceLabel}` : date;
}

/**
 * Muted provenance label for a pinned race's origin (spec §3.4). Duplicated
 * (not imported) so the wizard's selected-card and this pinned list agree
 * without either owning the other. Returns null for an unknown source.
 */
function pinnedRaceSourceLabel(source?: RaceSource): string | null {
  switch (source) {
    case "event":
      return "Saved to your profile";
    case "journey":
      return "From your training plan";
    case "anon_draft":
      return "Draft — not saved yet";
    default:
      return null;
  }
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const q = query.trim().toLowerCase();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="fgo-event-opt__hit">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

/**
 * Race catalog autocomplete — `GET /api/events/search` (v3, anon-capable).
 * Selecting a hit calls {@link onPickSuggested} with the full {@link CatalogEventDTO}.
 */
export type EventSearchFieldHandle = {
  focus: () => void;
};

export type EventSearchFieldProps = {
  id: string;
  /** Accessible name for the search input (the visible field label was removed). */
  ariaLabel?: string;
  value: string;
  onChange: (next: string) => void;
  onPickSuggested?: (hit: CatalogEventDTO) => void;
  onOpenAddCustom?: (presetName: string) => void;
  /** Races already on the profile, shown as "Your races" at the top of the panel. */
  pinnedRaces?: PinnedRaceOption[];
  /** Invoked when a pinned race is chosen (reuses the existing event). */
  onPickPinned?: (race: PinnedRaceOption) => void;
  /** Overrides the default input placeholder (e.g. "Search to change your race…"). */
  placeholder?: string;
  /** When set, opening the field with an empty query loads catalog rows from this date. */
  browseFromYmd?: string;
};

export const EventSearchField = forwardRef<EventSearchFieldHandle, EventSearchFieldProps>(
  function EventSearchField(
    {
      id,
      ariaLabel,
      value,
      onChange,
      onPickSuggested,
      onOpenAddCustom,
      pinnedRaces,
      onPickPinned,
      placeholder,
      browseFromYmd
    },
    ref
  ) {
  const panelId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      setOpen(true);
    }
  }));

  const [panelGeo, setPanelGeo] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: PANEL_HEIGHT_CAP,
    placement: "below" as "above" | "below"
  });
  const [open, setOpen] = useState(false);
  const [apiRows, setApiRows] = useState<CatalogEventDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [browseRows, setBrowseRows] = useState<CatalogEventDTO[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseFetchError, setBrowseFetchError] = useState(false);

  const trimmed = value.trim();
  const inSearchMode = trimmed.length >= MIN_QUERY_LEN;
  const listRows = inSearchMode ? apiRows : browseFromYmd ? browseRows : [];
  const listLoading = inSearchMode ? loading : browseLoading;
  const listFetchError = inSearchMode ? fetchError : browseFetchError;

  function isAbortError(err: unknown): boolean {
    const name =
      typeof err === "object" &&
      err !== null &&
      "name" in err &&
      typeof (err as { name?: string }).name === "string"
        ? (err as { name: string }).name
        : "";
    return name === "AbortError";
  }

  useEffect(() => {
    if (trimmed.length < MIN_QUERY_LEN) {
      setApiRows([]);
      setLoading(false);
      setFetchError(false);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setFetchError(false);

    const timer = window.setTimeout(() => {
      void searchCatalogEvents(trimmed, { limit: 10, signal: ac.signal })
        .then((rows) => setApiRows(rows))
        .catch((err: unknown) => {
          if (isAbortError(err)) return;
          setFetchError(true);
          setApiRows([]);
        })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [trimmed]);

  useEffect(() => {
    if (!open || inSearchMode || !browseFromYmd) {
      setBrowseRows([]);
      setBrowseLoading(false);
      setBrowseFetchError(false);
      return;
    }

    const ac = new AbortController();
    setBrowseLoading(true);
    setBrowseFetchError(false);

    void browseCatalogEvents(browseFromYmd, { limit: 10, signal: ac.signal })
      .then((rows) => setBrowseRows(rows))
      .catch((err: unknown) => {
        if (isAbortError(err)) return;
        setBrowseFetchError(true);
        setBrowseRows([]);
      })
      .finally(() => setBrowseLoading(false));

    return () => ac.abort();
  }, [open, inSearchMode, browseFromYmd]);

  const matchKey = listRows.map((m) => m.id).join("|");
  const [highlightIndex, setHighlightIndex] = useState(-1);

  useEffect(() => {
    if (!open) return;
    if (listLoading) {
      setHighlightIndex(-1);
      return;
    }
    setHighlightIndex(listRows.length === 0 ? -1 : 0);
  }, [open, listLoading, matchKey, listRows.length]);

  const close = useCallback(() => setOpen(false), []);

  const measurePanel = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof window === "undefined") return;
    setPanelGeo(measureFloatingPanel(wrap.getBoundingClientRect()));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    measurePanel();
    const wrap = wrapRef.current;
    const ro =
      typeof ResizeObserver !== "undefined" && wrap
        ? new ResizeObserver(() => measurePanel())
        : null;
    if (wrap && ro) ro.observe(wrap);
    window.addEventListener("scroll", measurePanel, true);
    window.addEventListener("resize", measurePanel);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", measurePanel);
    vv?.addEventListener("scroll", measurePanel);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", measurePanel, true);
      window.removeEventListener("resize", measurePanel);
      vv?.removeEventListener("resize", measurePanel);
      vv?.removeEventListener("scroll", measurePanel);
    };
  }, [open, measurePanel, listRows.length, listLoading, trimmed.length]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(ev: MouseEvent) {
      const t = ev.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, close]);

  const portalTarget =
    typeof document !== "undefined" ? fgPortalMount() ?? document.body : null;

  function pickSuggestion(hit: CatalogEventDTO) {
    onChange(hit.name);
    onPickSuggested?.(hit);
    close();
    inputRef.current?.blur();
  }

  function pickPinned(race: PinnedRaceOption) {
    onChange(race.name);
    onPickPinned?.(race);
    close();
    inputRef.current?.blur();
  }

  const pinned = pinnedRaces ?? [];

  const useAddModal = typeof onOpenAddCustom === "function";
  const addDisabled = !useAddModal && trimmed.length === 0;

  function onAddRowClick() {
    if (addDisabled) return;
    if (useAddModal) {
      onOpenAddCustom(trimmed);
      close();
      inputRef.current?.blur();
    } else if (trimmed.length > 0) {
      onChange(trimmed);
      close();
      inputRef.current?.blur();
    }
  }

  function onListKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      inputRef.current?.blur();
      return;
    }
    if (listLoading || listRows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) =>
        listRows.length === 0 ? -1 : i < 0 ? 0 : Math.min(listRows.length - 1, i + 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? 0 : i - 1));
    } else if (
      e.key === "Enter" &&
      highlightIndex >= 0 &&
      listRows[highlightIndex]
    ) {
      e.preventDefault();
      pickSuggestion(listRows[highlightIndex]);
    }
  }

  const showPanel = open;
  const highlightedId =
    highlightIndex >= 0 && listRows[highlightIndex]
      ? `${panelId}-opt-${highlightIndex}`
      : undefined;

  const panelHints =
    showPanel &&
    (!trimmed.length ? (
      browseFromYmd || pinned.length === 0 ? (
        <div className="fgo-event-hint">
          Start typing — races and locations are matched after {MIN_QUERY_LEN} characters.
        </div>
      ) : null
    ) : trimmed.length < MIN_QUERY_LEN ? (
      <div className="fgo-event-hint">
        Type {MIN_QUERY_LEN}+ characters to search the race catalog.
      </div>
    ) : null);

  let listBody: ReactNode = null;
  if (showPanel && (inSearchMode || browseFromYmd)) {
    if (listLoading) {
      listBody = (
        <div className="fgo-event-hint fgo-event-hint--pad">
          {inSearchMode ? "Searching…" : "Loading…"}
        </div>
      );
    } else if (listFetchError) {
      listBody = inSearchMode ? (
        <div className="fgo-event-hint fgo-event-hint--warn">
          Couldn&apos;t load results. Use &quot;Add a custom event&quot; below, or try
          again.
        </div>
      ) : null;
    } else if (listRows.length === 0) {
      listBody = inSearchMode ? (
        <div className="fgo-event-empty">No catalog matches — add a custom event below.</div>
      ) : null;
    } else {
      listBody = (
        <>
          {listRows.map((ev, index) => {
            const selected = highlightIndex === index;
            const meta = formatCatalogEventSubtitle(ev);
            return (
              <button
                key={ev.id}
                id={`${panelId}-opt-${index}`}
                type="button"
                role="option"
                aria-selected={selected}
                className={`fgo-event-opt fgo-event-opt--row${selected ? " is-active" : ""}`}
                onMouseDown={(press) => press.preventDefault()}
                onMouseEnter={() => setHighlightIndex(index)}
                onClick={() => pickSuggestion(ev)}>
                <span className="fgo-event-opt__ico" aria-hidden>
                  <LucideIcon name="flag" size={18} color="#B45309" strokeWidth={2} />
                </span>
                <span className="fgo-event-opt__body">
                  <span className="fgo-event-opt__title">
                    <HighlightMatch text={ev.name} query={value} />
                  </span>
                  <span className="fgo-event-opt__meta">{meta}</span>
                </span>
                <span className="fgo-event-opt__pick" aria-hidden>Pick →</span>
              </button>
            );
          })}
        </>
      );
    }
  }

  const floatedPanel =
    showPanel &&
    portalTarget &&
    createPortal(
      <div
        ref={panelRef}
        id={panelId}
        className={`fgo-event-panel fgo-event-panel--floating${
          panelGeo.placement === "above" ? " fgo-event-panel--above" : ""
        }`}
        style={{
          top: panelGeo.top,
          left: panelGeo.left,
          width: panelGeo.width,
          maxHeight: panelGeo.maxHeight,
          boxSizing: "border-box"
        }}>
        <div role="listbox" aria-label="Catalog races" className="fgo-event-panel__scroll">
          {pinned.length > 0 && (
            <>
              <div className="fgo-event-section-head">Your races</div>
              {pinned.map((race) => (
                <button
                  key={race.customerEventId ?? `${race.name}|${race.dateYmd}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="fgo-event-opt fgo-event-opt--row"
                  onMouseDown={(press) => press.preventDefault()}
                  onClick={() => pickPinned(race)}>
                  <span className="fgo-event-opt__ico" aria-hidden>
                    <LucideIcon name="flag" size={18} color="#B45309" strokeWidth={2} />
                  </span>
                  <span className="fgo-event-opt__body">
                    <span className="fgo-event-opt__title">{race.name}</span>
                    <span className="fgo-event-opt__meta">{formatPinnedRaceMeta(race)}</span>
                    {pinnedRaceSourceLabel(race.source) && (
                      <span
                        className="fgo-event-opt__meta"
                        style={{ fontSize: 12, color: "var(--fg-gray-700)", opacity: 0.85 }}>
                        {pinnedRaceSourceLabel(race.source)}
                      </span>
                    )}
                  </span>
                  <span className="fgo-event-opt__pick" aria-hidden>Pick →</span>
                </button>
              ))}
            </>
          )}
          {panelHints}
          {listBody}
        </div>
        <button
          type="button"
          className={`fgo-event-add${addDisabled ? " fgo-event-add--dim" : ""}`}
          aria-disabled={addDisabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onAddRowClick}>
          <span className="fgo-event-add__inner">
            <span className="fgo-event-add__muted">Can&apos;t find it?</span>{" "}
            <span className="fgo-event-add__cta">Add a custom event</span>
            <span className="fgo-event-add__arrow" aria-hidden>
              <LucideIcon name="chevron-right" size={16} />
            </span>
          </span>
        </button>
      </div>,
      portalTarget
    );

  return (
    <div
      ref={wrapRef}
      className={`fgo-event-ddwrap${showPanel ? " fgo-event-ddwrap--open" : ""}${
        showPanel && panelGeo.placement === "below" ? " fgo-event-ddwrap--open-below" : ""
      }${showPanel && panelGeo.placement === "above" ? " fgo-event-ddwrap--open-above" : ""}`}>
      <span className="fgo-event-search-ico" aria-hidden>
        <LucideIcon name="search" size={18} color="currentColor" strokeWidth={2} />
      </span>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="fg-input"
        autoComplete="off"
        autoCapitalize="words"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={showPanel}
        aria-controls={panelId}
        aria-activedescendant={highlightedId}
        placeholder={placeholder ?? "Search the race catalog (e.g. Boston Marathon)…"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={onListKeyDown}
      />
      {floatedPanel}
    </div>
  );
});
